import axios from 'axios';

// API base URL (will work in both development and production)
const API_URL = process.env.NODE_ENV === 'production' 
  ? `${window.location.origin}/api` // In production, use the current domain with /api prefix
  : 'http://localhost:5002/api'; // In development, use the local server with /api prefix

// Token storage keys
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Create axios instance with auth headers
const createAuthInstance = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
};

// Auth API service
const authApi = {
  // Register a new user
  register: async (email, password, displayName, role = 'student', school = '', teacherCode = '') => {
    try {
      const registerEndpoint = `${API_URL}/auth/register`;
      console.log('Register request to:', registerEndpoint);
      
      // Configure request with detailed logging
      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true // Enable credentials for CORS
      };
      
      // Log the full request details
      console.log('Register request config:', {
        url: registerEndpoint,
        method: 'POST',
        headers: config.headers,
        data: { email, password: '***', displayName, role, school, teacherCode }
      });
      
      const response = await axios.post(
        registerEndpoint,
        { email, password, displayName, role, school, teacherCode },
        config
      );
      
      console.log('Registration response received:', {
        status: response.status,
        data: response.data ? { ...response.data, token: '***' } : null
      });
      
      if (response.data && response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        console.log('Registration successful, user data stored');
      }
      
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response) {
        console.error('Server response:', error.response.status, error.response.data);
        throw new Error(error.response.data?.error || 'Registration failed');
      } else if (error.request) {
        console.error('No response received:', error.request);
        throw new Error('No response from server. Please check your connection');
      } else {
        console.error('Request error:', error.message);
        throw new Error(error.message || 'An error occurred during registration');
      }
    }
  },
  
  // Login a user
  login: async (email, password) => {
    try {
      const loginEndpoint = `${API_URL}/auth/login`;
      console.log('Login request to:', loginEndpoint);
      console.log('With credentials:', { email, passwordLength: password ? password.length : 0 });
      
      // Configure request with detailed logging
      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true // Enable credentials for CORS
      };
      
      // Log the full request details
      console.log('Login request config:', {
        url: loginEndpoint,
        method: 'POST',
        headers: config.headers,
        data: { email, password: '***' }
      });
      
      const response = await axios.post(
        loginEndpoint, 
        { email, password },
        config
      );
      
      console.log('Login response received:', {
        status: response.status,
        data: response.data ? { ...response.data, token: '***' } : null
      });
      
      if (response.data && response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        console.log('Login successful, user data stored');
        return response.data;
      } else {
        console.error('Invalid response format:', response.data);
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Log detailed error information
      if (error.response) {
        console.error('Server error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        throw new Error(error.response.data?.error || 'Authentication failed');
      } else if (error.request) {
        console.error('No response received:', {
          request: {
            method: error.request.method,
            url: error.request.url,
            headers: error.request.headers
          }
        });
        throw new Error('No response from server. Please check your connection');
      } else {
        console.error('Request setup error:', error.message);
        throw new Error(error.message || 'An error occurred during login');
      }
    }
  },
  
  // Logout a user
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  
  // Reset password
  resetPassword: async (email) => {
    try {
      console.log('Reset password request to:', `${API_URL}/auth/reset-password`);
      
      const response = await axios.post(`${API_URL}/auth/reset-password`, { email });
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.response) {
        console.error('Server response:', error.response.status, error.response.data);
        throw new Error(error.response.data?.error || 'Password reset failed');
      } else if (error.request) {
        console.error('No response received:', error.request);
        throw new Error('No response from server. Please check your connection');
      } else {
        console.error('Request error:', error.message);
        throw new Error(error.message || 'An error occurred during password reset');
      }
    }
  },
  
  // Get current user from token
  getCurrentUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  
  // Get user data from server
  getUserData: async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      
      if (!token) {
        return null;
      }
      
      console.log('Get user data request to:', `${API_URL}/auth/user`);
      
      const response = await axios.get(`${API_URL}/auth/user`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Update stored user data with fresh data
      if (response.data) {
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Get user data error:', error);
      // If unauthorized, clear token and user data
      if (error.response && error.response.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
      throw new Error(error.message || 'Failed to get user data');
    }
  },
  
  // Update user role (admin only)
  updateUserRole: async (uid, role) => {
    try {
      const authInstance = createAuthInstance();
      console.log('Update user role request to:', `${API_URL}/auth/user/${uid}`);
      
      const response = await authInstance.patch(`${API_URL}/auth/user/${uid}`, { role });
      return response.data;
    } catch (error) {
      console.error('Update user role error:', error);
      // Handle unauthorized errors by clearing token
      if (error.response && error.response.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
      throw new Error(error.response?.data?.error || error.message || 'Failed to update user role');
    }
  },
  
  // Get auth token
  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },
  
  // Check if token exists and is not expired
  isAuthenticated: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    // JWT tokens have three parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    try {
      // The middle part is the payload, which we decode to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      // Check if token is expired
      if (payload.exp && payload.exp < now) {
        // Clear expired token
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return false;
      }
      
      return true;
    } catch (e) {
      return false;
    }
  }
};

export default authApi; 